import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, ReferenceLine, ReferenceDot, Tooltip,
} from 'recharts';
import { chart, axisTick, formatMoney, formatShort } from '../../../theme/chart';
import type { CumulativeSeries } from './dashboard.types';

/**
 * The month's run, inside "Ritmo dos gastos": an area through today plus a
 * dashed line to the end of the month. Pay day becomes a vertical mark instead of a second line.
 * A month that is over is all area: its end is what it spent.
 */
export function CumulativeSpendChart({ actual, projectedEnd, today, daysInMonth, incomeDay, incomeAmount, budget, closed, currency }: CumulativeSeries & { currency: string }) {
  const { t, i18n } = useTranslation('dashboard');
  const locale = i18n.language;
  const money = (value: number) => formatMoney(value, currency, locale);
  const byDay = new Map(actual.map((entry) => [entry.day, entry.cumulative]));
  const todayValue = byDay.get(today) ?? actual[actual.length - 1]?.cumulative ?? 0;
  const slope = daysInMonth > today ? (projectedEnd - todayValue) / (daysInMonth - today) : 0;

  const data = Array.from({ length: daysInMonth }, (_, index) => {
    const day = index + 1;
    return {
      day,
      actual: day <= today ? byDay.get(day) ?? null : null,
      projected: !closed && day >= today ? todayValue + slope * (day - today) : null,
    };
  });

  // The dot's caption sits above it, centered — or ends at it when the dot
  // is near the month's end, so the text never runs off the plot.
  const dotText = closed ? money(todayValue) : `${t('home.today')} · ${money(todayValue)}`;
  const nearEnd = today > daysInMonth * 0.8;
  const withinBudget = budget !== undefined ? projectedEnd <= budget : undefined;
  const yMax = Math.max(projectedEnd, budget ?? 0, 1) * 1.1;
  // Just under the budget line, a caption above the dot would sit on it: put it below instead.
  const captionBelow = budget !== undefined && todayValue <= budget && (budget - todayValue) / yMax < 0.14;
  const dotLabel = ({ viewBox }: { viewBox?: { x: number; y: number; width: number } }) => {
    if (!viewBox) return <text />;
    const cx = viewBox.x + viewBox.width / 2;
    return (
      <text x={nearEnd ? cx - 8 : cx} y={captionBelow ? viewBox.y + 22 : viewBox.y - 8} textAnchor={nearEnd ? 'end' : 'middle'} fontSize={12} fontWeight={700} fill={chart.ink}>
        {dotText}
      </text>
    );
  };

  const ticks = Array.from(new Set([1, 5, 10, 15, 20, 25, daysInMonth].filter((day) => day <= daysInMonth)));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <ResponsiveContainer width="100%" height={200}>
          <ComposedChart data={data} margin={{ top: 14, right: 16, left: -4, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke={chart.grid} />
            <XAxis dataKey="day" tickLine={false} axisLine={false} tick={axisTick} ticks={ticks} />
            <YAxis tickLine={false} axisLine={false} tick={axisTick} tickFormatter={(value) => (value ? formatShort(Number(value), locale) : '0')} domain={[0, yMax]} width={64} />
            <Tooltip
              formatter={(value, name) => [money(Number(value)), String(name) === 'actual' ? t('home.spent') : t('home.projection')]}
              labelFormatter={(day) => t('home.dayN', { day })}
              contentStyle={{ fontSize: 12, borderRadius: 8, border: `1px solid ${chart.grid}` }}
            />
            {incomeDay && (
              <ReferenceLine
                x={incomeDay}
                stroke="#C9D2D0"
                strokeDasharray="2 4"
                label={{ value: incomeAmount ? `${t('home.salary')} +${money(incomeAmount)}` : t('home.salary'), position: 'insideBottomLeft', fontSize: 11, fill: chart.inkSecondary }}
              />
            )}
            {budget !== undefined && budget <= yMax && (
              <ReferenceLine y={budget} stroke={chart.spend} strokeDasharray="4 4" label={{ value: t('home.budget'), position: 'insideBottomLeft', fontSize: 11, fill: chart.spend }} />
            )}
            <Area type="monotone" dataKey="actual" stroke={chart.primary} strokeWidth={2} fill={chart.primaryFill} connectNulls={false} isAnimationActive={false} dot={false} activeDot={{ r: 4 }} />
            <Line type="linear" dataKey="projected" stroke={chart.primary} strokeWidth={2} strokeDasharray="5 5" dot={false} activeDot={false} isAnimationActive={false} />
            <ReferenceDot x={today} y={todayValue} r={4.5} fill={chart.primary} stroke="#fff" strokeWidth={2} label={dotLabel} />
          </ComposedChart>
        </ResponsiveContainer>
        <Box component="span" sx={{ fontSize: 13, color: chart.inkMuted }}>
          {closed
            ? `${t('home.monthTotal')}: ${money(projectedEnd)}`
            : t('home.rhythm.legend')}
          {closed && withinBudget !== undefined && ` · ${withinBudget ? t('home.withinBudget') : t('home.overBudget')}`}
        </Box>
    </Box>
  );
}
